import Add from '@mui/icons-material/Add';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import Edit from '@mui/icons-material/Edit';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress, {
  circularProgressClasses,
} from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  FileAttachmentCategory,
  patchV1CompaniesCompanyIdJobsJobIdSummary,
  RateType,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { isNil, noop } from 'lodash-es';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import JobSummaryCard from '~components/Job/JobSummaryCard';
import { JobSummaryEditFormHandler } from '~components/Job/JobSummaryEditForm';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { FeatureFlags } from '~constants/featureFlags';
import { trackApprovalBatch } from '~hooks/useAnalytics/trackApprovals';
import { useFileAttachment } from '~hooks/useFileAttachment/useFileAttachment';
import { Job, JobSummary } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import { Timesheet as TimesheetRecord } from '~hooks/useTimesheets/models';
import useTimesheets from '~hooks/useTimesheets/useTimesheets';
import useTimesheetScanPolling from '~hooks/useTimesheets/useTimesheetScanPolling';
import useWorkLock from '~hooks/useWorkLock/useWorkLock';
import ApprovalActionsMenu from '~pages/Approvals/DriverDay/components/ApprovalActionsMenu';
import TimesheetDrawer from '~pages/Approvals/shared/TimesheetDrawer/TimesheetDrawer';
import {
  fileAttachmentToListItem,
  TimesheetListItem,
  timesheetToListItem,
} from '~pages/Approvals/shared/timesheetListItem';
import useApprovalCardShadow from '~pages/Approvals/shared/useApprovalCardShadow';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { minutesToDecimalHours } from '~utils/dateTime';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { extractRateInfoFromJob, getApprovalStatus } from '~utils/jobs/job-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import JobTimesheetRow, { JobTimesheetRowState } from './JobTimesheetRow';

const MAX_FILE_SIZE_IN_MB = 10;
const MAX_VISIBLE_TIMESHEETS = 3;
const TIME_FORMAT = 'hh:mm A';
// Both the stat row's controls and the timesheet row's action sit in a column of this width,
// so the flexible area beside them resolves identically and their columns stay aligned.
const ACTION_COLUMN_WIDTH_IN_PX = 200;
// Matches the decimal-hours precision the job's own Total Hrs stat renders, so the reported
// total sitting beneath it can be compared without converting between formats.
const HOURS_PRECISION = 2;
const HOURLY_RATE_SIDES = ['customer', 'pay', 'hauler', 'driver'] as const;

interface ReportedTimes {
  approvedWorkTimeMinutes: number | null;
  loggingEndedAt: Dayjs | null;
  loggingStartedAt: Dayjs | null;
}

function getTimesheetRowDetails({
  isJobLocked,
  isScanning,
  jobSummary,
  timesheet,
}: {
  isJobLocked: boolean;
  isScanning: boolean;
  jobSummary: JobSummary | null | undefined;
  timesheet: TimesheetListItem;
}) {
  const reportedMinutes = timesheet.totalMinutes;
  const reportedHours = minutesToDecimalHours(reportedMinutes);
  const hasReportedTimes = Boolean(
    timesheet.startedAt || timesheet.endedAt || reportedMinutes !== null,
  );

  const billableMinutes =
    jobSummary?.approvedWorkTimeMinutes ?? jobSummary?.workTimeMinutes;
  const jobStartedAt =
    jobSummary?.adjustedLoggingStartedAt ?? jobSummary?.loggingStartedAt ?? null;
  const jobEndedAt =
    jobSummary?.adjustedLoggingEndedAt ?? jobSummary?.loggingEndedAt ?? null;

  // A time the timesheet doesn't report can't differ from the job: applying leaves it alone.
  const startMatchesJob =
    !timesheet.startedAt ||
    (jobStartedAt !== null && timesheet.startedAt.isSame(jobStartedAt, 'minute'));
  const endMatchesJob =
    !timesheet.endedAt ||
    (jobEndedAt !== null && timesheet.endedAt.isSame(jobEndedAt, 'minute'));
  const totalMatchesJob =
    reportedMinutes === null ||
    (!isNil(billableMinutes) && reportedMinutes === Math.round(billableMinutes));
  const matchesJob = startMatchesJob && endMatchesJob && totalMatchesJob;

  // A scan that stalls never sets aiProcessed, so times already on the timesheet win over it.
  let state: JobTimesheetRowState = 'reported';
  if (isScanning && !hasReportedTimes) {
    state = 'scanning';
  } else if (!hasReportedTimes) {
    state = 'noTimes';
  } else if (isJobLocked) {
    state = 'locked';
  } else if (matchesJob) {
    state = 'matches';
  }

  const reportedTimes: ReportedTimes = {
    approvedWorkTimeMinutes: reportedMinutes,
    loggingEndedAt: timesheet.endedAt,
    loggingStartedAt: timesheet.startedAt,
  };

  return {
    reportedTimes,
    reportedValues: [
      {
        key: 'start',
        label: t('approvals.timesheets.start'),
        value: timesheet.startedAt
          ? dayjs.tz(timesheet.startedAt).format(TIME_FORMAT)
          : null,
      },
      {
        key: 'end',
        label: t('approvals.timesheets.end'),
        value: timesheet.endedAt ? dayjs.tz(timesheet.endedAt).format(TIME_FORMAT) : null,
      },
      {
        key: 'total',
        label: t('common.total'),
        value:
          reportedHours === null ? null : `${reportedHours.toFixed(HOURS_PRECISION)}h`,
      },
    ],
    state,
  };
}

interface JobTimesheetsCardProps {
  job: Job;
  onJobUpdated: (job: Job) => void;
}

export default function JobTimesheetsCard({ job, onJobUpdated }: JobTimesheetsCardProps) {
  const theme = useTheme();
  const { toasterStore, userStore } = useStores();
  const { deleteFileAttachmentById } = useFileAttachment();
  const { createTimesheet, deleteTimesheetById } = useTimesheets();
  const { approveJob, unapproveJob } = useJob();
  const companyId = job.company?.id ?? getEffectiveUserCompanyId(userStore);
  const userPermissions = userStore.getPermissions();

  const useNewTimesheet = useFeatureFlag({
    featureFlagKey: FeatureFlags.useNewTimesheet,
  });
  const chargeBasedSettlements = useFeatureFlag({
    featureFlagKey: FeatureFlags.chargeBasedSettlements,
  });
  const cardShadow = useApprovalCardShadow();
  const approvalAlsoApprovesLoads = chargeBasedSettlements && Boolean(job.loadsCount);
  const timesheets = useJobTimesheets(job.id, companyId, useNewTimesheet);

  const [isEditing, setIsEditing] = useState(false);
  const [isChangingApprovalStatus, setIsChangingApprovalStatus] = useState(false);

  const [isShowingAllTimesheets, setIsShowingAllTimesheets] = useState(false);
  const [timesheetToDelete, setTimesheetToDelete] = useState<{
    timesheet?: TimesheetListItem;
    deleting: boolean;
  }>({ deleting: false });
  const [drawerTimesheet, setDrawerTimesheet] = useState<TimesheetRecord | null>(null);
  const [isAddingTimesheet, setIsAddingTimesheet] = useState(false);

  const canEditJobSummary = Boolean(job.jobSummary?.editable);
  const hoursLock = useWorkLock({
    billingApprovedAt: job.billingApprovedAt,
    editable: job.jobSummary?.editable ?? null,
    fieldApprovedAt: job.fieldApprovedAt,
    hasEditPermission: userPermissions.canEditJob,
  });
  const jobSummaryEditFormRef = useRef<JobSummaryEditFormHandler | null>(null);
  const [isSubmittingEditJobSummaryForm, setIsSubmittingEditJobSummaryForm] =
    useState(false);

  const [pendingScanTimesheetId, setPendingScanTimesheetId] = useState<string | null>(
    null,
  );

  const [isApplyingDriverTimes, setIsApplyingDriverTimes] = useState(false);
  const [timesToConfirm, setTimesToConfirm] = useState<ReportedTimes | null>(null);

  const visibleTimesheets = useMemo(() => {
    if (isShowingAllTimesheets) {
      return timesheets.files;
    }
    return timesheets.files.slice(0, MAX_VISIBLE_TIMESHEETS);
  }, [isShowingAllTimesheets, timesheets.files]);

  const {
    isApproved: isJobApproved,
    isUnapprovable: isJobUnapprovable,
    isApprovable: isJobApprovable,
  } = getApprovalStatus(job, userPermissions, Boolean(job.order?.editable));

  const handleToggleEditing = useCallback(() => {
    setIsEditing((state) => !state);
  }, []);

  const handleScanSettled = useCallback(
    (scannedTimesheet: TimesheetRecord | null) => {
      setPendingScanTimesheetId(null);

      if (!scannedTimesheet) {
        return;
      }

      // The drawer opens from the record map built at fetch time, which still holds the
      // pre-scan snapshot; refresh it so the sheet shows its scanned values.
      timesheets.refetch();
      // Only swap in the scanned record if the approver is still looking at that sheet;
      // closing the drawer mid-scan must not reopen it.
      setDrawerTimesheet((openTimesheet) =>
        openTimesheet?.id === scannedTimesheet.id ? scannedTimesheet : openTimesheet,
      );
    },
    [timesheets.refetch],
  );

  useTimesheetScanPolling({
    companyId,
    onSettled: handleScanSettled,
    timesheetId: pendingScanTimesheetId,
  });

  const handleCloseDeleteTimesheetDialog = useCallback(() => {
    setTimesheetToDelete({ deleting: false });
  }, []);

  const handleDeleteTimesheet = useCallback(async () => {
    if (!timesheetToDelete.timesheet) {
      return;
    }

    setTimesheetToDelete((state) => ({ ...state, deleting: true }));

    try {
      if (timesheetToDelete.timesheet.source === 'timesheet' && companyId) {
        await deleteTimesheetById(companyId, timesheetToDelete.timesheet.deleteId);
      } else {
        await deleteFileAttachmentById(timesheetToDelete.timesheet.deleteId);
      }

      timesheets.updateTimesheets(timesheetToDelete.timesheet, true);
      toasterStore.push(
        alert(
          t('approvals.driver_day.time_sheet_removed_successfully'),
          AlertTypes.success,
        ),
      );

      setTimesheetToDelete({ deleting: false });
    } catch {
      setTimesheetToDelete((state) => ({ ...state, deleting: false }));
    }
  }, [
    timesheetToDelete.timesheet,
    timesheets.updateTimesheets,
    companyId,
    deleteFileAttachmentById,
    deleteTimesheetById,
  ]);

  const handleEditJobSummaryFormSubmit = useCallback(async () => {
    const formData = await jobSummaryEditFormRef.current?.onSubmit();

    if (!formData) {
      return;
    }

    setIsSubmittingEditJobSummaryForm(true);

    try {
      const response = await patchV1CompaniesCompanyIdJobsJobIdSummary({
        path: { 'company-id': companyId, 'job-id': job.id },
        body: {
          adjusted_logging_started_at: formData.loggingStartedAt?.toISOString() ?? null,
          adjusted_logging_ended_at: formData.loggingEndedAt?.toISOString() ?? null,
          approved_work_time_minutes: formData.approvedWorkTimeMinutes,
        },
      });
      job.jobSummary = JobSummary.parse(response.data.data);
      onJobUpdated(job);

      toasterStore.push(
        alert(t('approvals.approve_jobs.job_summary_updated'), AlertTypes.success),
      );
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_update_job_summary'),
      );
    } finally {
      setIsEditing(false);
      setIsSubmittingEditJobSummaryForm(false);
    }
  }, [companyId, job.id, onJobUpdated]);

  const isJobLocked = Boolean(job.billingApprovedAt || job.fieldApprovedAt);
  // Any Per Hour side (customer, vendor or driver) is billed or paid from these hours.
  const isBilledByTheHour = HOURLY_RATE_SIDES.some(
    (rateSide) =>
      extractRateInfoFromJob(job, rateSide)?.rateType === RateType.RATE_PER_HOUR,
  );

  const timesheetRows = useMemo(
    () =>
      visibleTimesheets.map((timesheet) => {
        const isScanning =
          timesheet.source === 'timesheet' &&
          timesheet.deleteId === pendingScanTimesheetId;

        return {
          ...getTimesheetRowDetails({
            isJobLocked,
            isScanning,
            jobSummary: job.jobSummary,
            timesheet,
          }),
          timesheet,
        };
      }),
    [isJobLocked, job.jobSummary, pendingScanTimesheetId, visibleTimesheets],
  );

  const applyDriverReportedTimes = useCallback(
    async (reportedTimes: ReportedTimes) => {
      setIsApplyingDriverTimes(true);

      try {
        // The summary PATCH only touches the keys it receives, and a sent null clears the
        // job's value. A timesheet missing a time must leave the job's own value in place.
        const response = await patchV1CompaniesCompanyIdJobsJobIdSummary({
          path: { 'company-id': companyId, 'job-id': job.id },
          body: {
            ...(reportedTimes.loggingStartedAt && {
              adjusted_logging_started_at: reportedTimes.loggingStartedAt.toISOString(),
            }),
            ...(reportedTimes.loggingEndedAt && {
              adjusted_logging_ended_at: reportedTimes.loggingEndedAt.toISOString(),
            }),
            ...(reportedTimes.approvedWorkTimeMinutes !== null && {
              approved_work_time_minutes: reportedTimes.approvedWorkTimeMinutes,
            }),
          },
        });
        job.jobSummary = JobSummary.parse(response.data.data);
        onJobUpdated(job);

        toasterStore.push(
          alert(t('approvals.approve_jobs.job_summary_updated'), AlertTypes.success),
        );
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.jobs.failed_to_update_job_summary'),
        );
      } finally {
        setIsApplyingDriverTimes(false);
        setTimesToConfirm(null);
      }
    },
    [companyId, job, onJobUpdated, toasterStore],
  );

  const handleUseDriverTimes = useCallback(
    (reportedTimes: ReportedTimes) => {
      const summary = job.jobSummary;
      // Only the adjusted span marks a correction someone made by hand. The approved total is
      // also written from the job's hourly tickets, so warning on it would claim the approver
      // is about to lose work they never did.
      const applyReplacesAdjustedStart = Boolean(
        reportedTimes.loggingStartedAt && summary?.adjustedLoggingStartedAt,
      );
      const applyReplacesAdjustedEnd = Boolean(
        reportedTimes.loggingEndedAt && summary?.adjustedLoggingEndedAt,
      );

      if (applyReplacesAdjustedStart || applyReplacesAdjustedEnd) {
        setTimesToConfirm(reportedTimes);

        return;
      }

      applyDriverReportedTimes(reportedTimes);
    },
    [applyDriverReportedTimes, job.jobSummary],
  );

  const handleCloseDriverTimesConfirmation = useCallback(() => {
    if (isApplyingDriverTimes) {
      return;
    }

    setTimesToConfirm(null);
  }, [isApplyingDriverTimes]);

  const handleCreateTimesheetFromImage = useCallback(
    async (file: File) => {
      if (!companyId) {
        console.warn('Skipped creating a timesheet from a slip: no company id');

        return;
      }

      try {
        const createdTimesheet = await createTimesheet(companyId, {
          file,
          jobId: job.id,
        });
        toasterStore.push(
          alert(
            t('approvals.driver_day.time_sheet_added_successfully'),
            AlertTypes.success,
          ),
        );
        // Refetch (silently) rather than optimistically pushing the row: the fetch rebuilds
        // the full-record lookup map the list's rows open the drawer from.
        timesheets.refetch();
        setIsAddingTimesheet(false);
        setDrawerTimesheet(createdTimesheet);

        if (!createdTimesheet.aiProcessed) {
          setPendingScanTimesheetId(createdTimesheet.id);
        }
      } catch {
        // createTimesheet has already shown the error; the drawer stays on the drop zone so
        // the approver can try again.
      }
    },
    [companyId, createTimesheet, job.id, timesheets.refetch, toasterStore],
  );

  // Adding a timesheet never writes the job's hours, so approval doesn't lock it; only
  // Use Timesheet Times does.
  const canAddTimesheet = useNewTimesheet
    ? Boolean(userPermissions.canCreateTicket) && Boolean(companyId)
    : true;

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box
          sx={{
            bgcolor: theme.semantic.surfaceRaised,
            border: `1px solid ${theme.brandV2.colors.treadGray7}`,
            borderRadius: 2,
            boxShadow: cardShadow,
            gap: 1.5,
            height: 'fit-content',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 3,
              p: 1.5,
              position: 'relative',
            }}
          >
            <Box sx={{ display: 'flex', flex: 4, flexDirection: 'column', gap: 1 }}>
              <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
                <Typography
                  sx={{
                    color: theme.brandV2.colors.treadBlack,
                    fontSize: '14px',
                    fontWeight: 600,
                  }}
                >
                  {isBilledByTheHour
                    ? t('approvals.approve_jobs.driver_reported.billable_hours')
                    : t('approvals.approve_jobs.driver_reported.job_times')}
                </Typography>

                {isBilledByTheHour && (
                  <Tooltip
                    title={t(
                      'approvals.approve_jobs.driver_reported.job_hours_billable_tip',
                    )}
                  >
                    <InfoOutlined
                      aria-label={t(
                        'approvals.approve_jobs.driver_reported.job_hours_billable_tip',
                      )}
                      sx={{ color: theme.brandV2.colors.treadGray4, fontSize: '14px' }}
                    />
                  </Tooltip>
                )}
              </Box>

              <JobSummaryCard
                ref={jobSummaryEditFormRef}
                isEditing={isEditing}
                isSubmittingForm={isSubmittingEditJobSummaryForm}
                job={job}
                sx={{ p: 0 }}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                justifyContent: 'flex-end',
                width: `${ACTION_COLUMN_WIDTH_IN_PX}px`,
              }}
            >
              {canEditJobSummary && (
                <EditCTA
                  disabled={isApplyingDriverTimes}
                  isEditing={isEditing}
                  onClick={handleToggleEditing}
                />
              )}

              {hoursLock.isLocked && (
                <Tooltip title={hoursLock.reason}>
                  <span data-ui="job-timesheets-card__hours-lock">
                    <EditCTA disabled isEditing={false} onClick={noop} />
                  </span>
                </Tooltip>
              )}

              {isEditing ? (
                <SmallLoadingButton
                  color="brandV2Green"
                  loading={isSubmittingEditJobSummaryForm}
                  onClick={handleEditJobSummaryFormSubmit}
                  size="small"
                  startIcon={<Check />}
                  variant="contained"
                  sx={{ fontWeight: 700 }}
                >
                  {t('actions.save')}
                </SmallLoadingButton>
              ) : isJobApproved ? (
                <ApprovalActionsMenu
                  approved={isJobApproved}
                  isLoading={isChangingApprovalStatus}
                  onMenuItemClick={
                    isJobUnapprovable
                      ? () => {
                          setIsChangingApprovalStatus(true);

                          unapproveJob(job.id, companyId)
                            .then((updatedJob) => {
                              if (updatedJob) {
                                onJobUpdated(updatedJob);
                              }
                            })
                            .finally(() => setIsChangingApprovalStatus(false));
                        }
                      : undefined
                  }
                />
              ) : isJobApprovable ? (
                <SmallLoadingButton
                  color="brandV2Green"
                  disabled={isEditing || isApplyingDriverTimes}
                  loading={isChangingApprovalStatus}
                  onClick={() => {
                    setIsChangingApprovalStatus(true);

                    approveJob(job.id, companyId)
                      .then((updatedJob) => {
                        if (updatedJob) {
                          trackApprovalBatch('job', 1);
                          onJobUpdated(updatedJob);
                        }
                      })
                      .finally(() => setIsChangingApprovalStatus(false));
                  }}
                  size="small"
                  startIcon={<Check />}
                  variant="contained"
                  sx={{ fontWeight: 700 }}
                >
                  {approvalAlsoApprovesLoads
                    ? t('approvals.approve_jobs.driver_reported.approve_hours_and_loads')
                    : t('actions.approve')}
                </SmallLoadingButton>
              ) : null}
            </Box>
          </Box>

          {!isEditing && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                mx: 1.5,
                pb: 1.5,
              }}
            >
              <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5 }}>
                <Typography
                  sx={{
                    color: theme.brandV2.colors.treadBlack,
                    fontSize: '14px',
                    fontWeight: 600,
                  }}
                >
                  {t('approvals.approve_jobs.driver_reported.timesheet')}
                </Typography>

                {canAddTimesheet && (
                  <AddTimeSheetCTA
                    jobId={job.id}
                    onAdd={useNewTimesheet ? () => setIsAddingTimesheet(true) : undefined}
                    onUploaded={() => timesheets.refetch()}
                  />
                )}
              </Box>

              {timesheets.loading && (
                <CircularProgress
                  size={16}
                  sx={{
                    [`& .${circularProgressClasses.svg}`]: {
                      color: theme.brandV2.colors.treadGray5,
                    },
                  }}
                />
              )}

              {!timesheets.loading &&
                timesheetRows.map(({ timesheet, ...row }) => {
                  const fullTimesheet =
                    timesheet.source === 'timesheet'
                      ? timesheets.getTimesheet(timesheet.key)
                      : undefined;
                  const canDeleteLegacyAttachment =
                    timesheet.source === 'fileAttachment' && timesheet.deletable;

                  return (
                    <JobTimesheetRow
                      key={timesheet.key}
                      // Old-style attachments predate reported times, so there are none to show.
                      hasReportedTimes={timesheet.source === 'timesheet'}
                      actionColumnWidthInPx={ACTION_COLUMN_WIDTH_IN_PX}
                      isApplying={isApplyingDriverTimes || isChangingApprovalStatus}
                      label={
                        timesheet.timesheetNumber ||
                        timesheet.fileName ||
                        t('actions.view')
                      }
                      onDelete={
                        canDeleteLegacyAttachment
                          ? () => setTimesheetToDelete({ deleting: false, timesheet })
                          : undefined
                      }
                      onOpen={() => {
                        if (fullTimesheet) {
                          setDrawerTimesheet(fullTimesheet);
                        } else if (timesheet.fileUrl) {
                          // Legacy file-attachment rows have no Timesheet record; open the image.
                          window.open(timesheet.fileUrl);
                        }
                      }}
                      onUseTimesheetHours={
                        row.state === 'reported' && canEditJobSummary
                          ? () => handleUseDriverTimes(row.reportedTimes)
                          : undefined
                      }
                      previewUrl={timesheet.previewUrl}
                      reportedValues={row.reportedValues}
                      state={row.state}
                    />
                  );
                })}

              {!timesheets.loading &&
                timesheets.files.length > MAX_VISIBLE_TIMESHEETS && (
                  <Button
                    color="brandV2OrangeDark"
                    onClick={() => setIsShowingAllTimesheets((state) => !state)}
                    variant="text"
                    sx={{
                      fontSize: '0.75rem',
                      height: 'fit-content',
                      minWidth: 'unset',
                      p: 0,
                      width: 'fit-content',
                    }}
                  >
                    {isShowingAllTimesheets
                      ? t('actions.show_less')
                      : t('actions.show_more')}
                  </Button>
                )}
            </Box>
          )}
        </Box>
      </Box>

      <Dialog
        isOpen={timesToConfirm !== null}
        onClose={handleCloseDriverTimesConfirmation}
      >
        <DialogHeader onClose={handleCloseDriverTimesConfirmation}>
          {t('approvals.approve_jobs.driver_reported.use_timesheet_times_title')}
        </DialogHeader>
        <DialogContent>
          <Typography>
            {t('approvals.approve_jobs.driver_reported.use_timesheet_times_message')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <SmallButton
            color="inherit"
            disableElevation
            disabled={isApplyingDriverTimes}
            onClick={handleCloseDriverTimesConfirmation}
            size="small"
            variant="outlined"
            sx={{ border: `1px solid ${theme.brandV2.colors.treadGray7}` }}
          >
            {t('actions.cancel')}
          </SmallButton>
          <SmallLoadingButton
            color="brandV2Green"
            loading={isApplyingDriverTimes}
            onClick={() => {
              if (timesToConfirm) {
                applyDriverReportedTimes(timesToConfirm);
              }
            }}
            size="small"
            variant="contained"
            sx={{ fontWeight: 700 }}
          >
            {t('approvals.approve_jobs.driver_reported.use_timesheet_times')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>

      <DeleteTimesheetDialog
        isDeleting={timesheetToDelete.deleting}
        onClose={handleCloseDeleteTimesheetDialog}
        onDelete={handleDeleteTimesheet}
        timesheetId={timesheetToDelete.timesheet?.key}
      />

      <TimesheetDrawer
        companyId={companyId}
        isScanningTimesheet={
          drawerTimesheet !== null && drawerTimesheet.id === pendingScanTimesheetId
        }
        onClose={() => {
          setDrawerTimesheet(null);
          setIsAddingTimesheet(false);
          // Re-fetch so a save/delete made inside the drawer is reflected in the list.
          timesheets.refetch();
        }}
        onCreateFromImage={isAddingTimesheet ? handleCreateTimesheetFromImage : undefined}
        onScanned={() => timesheets.refetch()}
        open={Boolean(drawerTimesheet) || isAddingTimesheet}
        timesheet={drawerTimesheet}
      />
    </>
  );
}

interface AddTimeSheetCTAProps {
  jobId: string;
  onAdd?: () => void;
  onUploaded: () => void;
}

function AddTimeSheetCTA({ jobId, onAdd, onUploaded }: AddTimeSheetCTAProps) {
  const theme = useTheme();
  const { toasterStore } = useStores();
  const { createFileAttachmentOnJob, updateFileAttachmentById } = useFileAttachment();
  const [isUploading, setIsUploading] = useState(false);

  const handleCreateFileAttachment = useCallback(
    async (file: File) => {
      if (file.size / 1024 / 1024 > MAX_FILE_SIZE_IN_MB) {
        toasterStore.push(
          alert(
            t('file_upload.max_size', { maxSize: MAX_FILE_SIZE_IN_MB + ' MB' }),
            AlertTypes.error,
          ),
        );

        return;
      }

      setIsUploading(true);

      try {
        const attachment = await createFileAttachmentOnJob(file, jobId);
        await updateFileAttachmentById(attachment.id, {
          category: FileAttachmentCategory.TIMESHEET,
        });

        toasterStore.push(
          alert(
            t('approvals.driver_day.time_sheet_added_successfully'),
            AlertTypes.success,
          ),
        );
        setIsUploading(false);
        onUploaded();
      } catch {
        setIsUploading(false);
      }
    },
    [
      createFileAttachmentOnJob,
      jobId,
      onUploaded,
      toasterStore,
      updateFileAttachmentById,
    ],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];

      if (file) {
        handleCreateFileAttachment(file);
      }
    },
    [handleCreateFileAttachment],
  );

  if (isUploading) {
    return (
      <CircularProgress
        size={16}
        sx={{
          [`& .${circularProgressClasses.svg}`]: {
            color: theme.brandV2.colors.treadGray5,
          },
        }}
      />
    );
  }

  return (
    <Typography
      component={onAdd ? 'button' : 'label'}
      onClick={onAdd}
      type={onAdd ? 'button' : undefined}
      sx={{
        alignItems: 'center',
        bgcolor: 'transparent',
        border: 'none',
        color: theme.brandV2.colors.treadBlue,
        cursor: 'pointer',
        display: 'flex',
        fontSize: '0.75rem',
        fontWeight: 600,
        gap: 0.25,
        p: 0,
      }}
    >
      {!onAdd && (
        <input
          accept="image/*"
          hidden
          maxLength={1}
          onChange={handleChange}
          onClick={(event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
            const element = event.target as HTMLInputElement;
            element.value = '';
          }}
          type="file"
        />
      )}
      <Add sx={{ fontSize: '14px' }} />
      {t('approvals.approve_jobs.driver_reported.add')}
    </Typography>
  );
}

interface EditCTAProps {
  disabled: boolean;
  isEditing: boolean;
  onClick: () => void;
}

function EditCTA({ disabled, isEditing, onClick }: EditCTAProps) {
  const theme = useTheme();

  return (
    <SmallButton
      color="inherit"
      disableElevation
      disabled={disabled}
      onClick={onClick}
      size="small"
      startIcon={isEditing ? <Close /> : <Edit />}
      variant="outlined"
      sx={{ border: `1px solid ${theme.brandV2.colors.treadGray7}` }}
    >
      {isEditing ? t('actions.cancel') : t('actions.edit')}
    </SmallButton>
  );
}

interface DeleteTimesheetDialogProps {
  isDeleting: boolean;
  onClose: () => void;
  onDelete: () => void;
  timesheetId?: string;
}

function DeleteTimesheetDialog({
  isDeleting,
  onClose,
  onDelete,
  timesheetId,
}: DeleteTimesheetDialogProps) {
  const closeDialog = useCallback(() => {
    if (!isDeleting) {
      onClose();
    }
  }, [isDeleting, onClose]);

  return (
    <ConfirmationDialog
      confirmButtonProps={{ children: t('actions.remove_time_sheet') }}
      isBusy={isDeleting}
      isOpen={Boolean(timesheetId)}
      onClose={closeDialog}
      onConfirm={onDelete}
      title={t('actions.remove_time_sheet')}
    >
      <Typography>{t('approvals.driver_day.confirm_removing_time_sheet')}</Typography>
    </ConfirmationDialog>
  );
}

function useJobTimesheets(
  jobId: string,
  companyId: string | undefined,
  useNewTimesheet: boolean,
) {
  const { getAllFileAttachmentsByJobId } = useFileAttachment();
  const { getTimesheetsOrEmpty } = useTimesheets();

  const [timesheets, setTimesheets] = useState<{
    loading: boolean;
    files: TimesheetListItem[];
  }>({ loading: true, files: [] });
  // The rows are normalized `TimesheetListItem`s; keep the full `Timesheet` records
  // (new-model rows only) so a click can open the drawer without a refetch. Keyed by the
  // list-item key (`timesheet:<id>`).
  const timesheetsByKeyRef = useRef<Map<string, TimesheetRecord>>(new Map());

  const fetchTimesheetByJobId = useCallback(
    async (jobId: string, options?: { silent?: boolean }) => {
      // A silent refresh (e.g. after a drawer close) keeps the current rows on screen
      // and skips the spinner, so the list does not flash empty when nothing changed.
      if (!options?.silent) {
        setTimesheets({ loading: true, files: [] });
      }

      const attachments = await getAllFileAttachmentsByJobId(jobId);
      const legacyItems = attachments
        .filter((file) => file.category === 'Timesheet')
        .map(fileAttachmentToListItem);

      if (useNewTimesheet && companyId) {
        // Dual-read: new Timesheet records plus any pre-existing legacy attachments, so
        // nothing disappears while the flag is being rolled out.
        const newTimesheets = await getTimesheetsOrEmpty(companyId, { jobId });
        timesheetsByKeyRef.current = new Map(
          newTimesheets.map((timesheet) => [`timesheet:${timesheet.id}`, timesheet]),
        );
        setTimesheets({
          loading: false,
          files: [...newTimesheets.map(timesheetToListItem), ...legacyItems],
        });
      } else {
        timesheetsByKeyRef.current = new Map();
        setTimesheets({ loading: false, files: legacyItems });
      }
    },
    [companyId, useNewTimesheet],
  );

  const getTimesheet = useCallback(
    (key: string) => timesheetsByKeyRef.current.get(key),
    [],
  );

  const updateTimesheets = useCallback((timesheet: TimesheetListItem, remove?: true) => {
    setTimesheets((state) => {
      const newFiles = [...state.files];

      if (remove) {
        const index = newFiles.findIndex((file) => file.key === timesheet.key);

        if (index > -1) {
          newFiles.splice(index, 1);
        }
      } else {
        newFiles.push(timesheet);
      }

      return { ...state, files: newFiles };
    });
  }, []);

  useEffect(() => {
    fetchTimesheetByJobId(jobId);
  }, [fetchTimesheetByJobId, jobId]);

  const refetch = useCallback(
    () => fetchTimesheetByJobId(jobId, { silent: true }),
    [fetchTimesheetByJobId, jobId],
  );

  return { ...timesheets, getTimesheet, refetch, updateTimesheets };
}
